import {
  COMMON_CHUNK_NAME,
  DEFAULT_LINK_AFTER,
  FUNCTION_DEFINE_CHUNK_NAME,
} from '../../../const/generator';

import {
  BuilderComponentPlugin,
  BuilderComponentPluginFactory,
  ChunkType,
  FileType,
  ICodeChunk,
  ICodeStruct,
  IContainerInfo,
} from '../../../types';

import {
  EnhancedStatement,
  TargetFile,
  getSubModuleFromTargetFile,
  getFileTypeFromTargetFile,
} from '../../../types/statements';

import {
  analyzeInternalDependencies,
  inferExportedStatements,
  generateInternalImportStatement,
  groupStatementsByTargetFile,
  collectExternalDependencies,
  addExportKeyword,
  ImportSpec,
} from '../../../utils/statementHelper';

import { isArray } from 'lodash';
import { mergeImportMaps } from '../../../utils/mergeImportMaps';

export interface PluginConfig {
  fileType: string;
}

/**
 * 获取 statement 对应的 linkAfter 值
 *
 * @param targetFile - 目标文件
 * @param isPrivate - 是否为 private（组件内部）
 * @returns linkAfter 数组
 */
function getLinkAfterForStatement(targetFile: TargetFile, isPrivate: boolean): string[] {
  if (targetFile === 'index.tsx') {
    if (isPrivate) {
      // private: true/undefined → 放在函数体内
      return [...DEFAULT_LINK_AFTER[FUNCTION_DEFINE_CHUNK_NAME.Start]];
    }
    // private: false → 放在模块级别（组件外部）
    return [...DEFAULT_LINK_AFTER[COMMON_CHUNK_NAME.FileVarDefine]];
  }
  // 其他文件（services.ts, constants.ts 等）放在导入之后
  return [COMMON_CHUNK_NAME.InternalDepsImport, COMMON_CHUNK_NAME.ExternalDepsImport];
}

/**
 * 获取 statement 对应的 chunk name
 *
 * @param targetFile - 目标文件
 * @param subModule - 子模块名称
 * @param isPrivate - 是否为 private（组件内部）
 * @returns chunk name
 */
function getChunkNameForStatement(
  targetFile: TargetFile,
  subModule: string,
  isPrivate: boolean,
): string {
  if (targetFile === 'index.tsx') {
    if (isPrivate) {
      // private: true/undefined → 放在函数体内
      return FUNCTION_DEFINE_CHUNK_NAME.InsStatement;
    }
    // private: false → 放在模块级别（组件外部）
    return COMMON_CHUNK_NAME.FileVarDefine;
  }
  // 其他文件
  return `${subModule}Statement`;
}

const pluginFactory: BuilderComponentPluginFactory<PluginConfig> = () => {
  const plugin: BuilderComponentPlugin = async (pre: ICodeStruct) => {
    const next: ICodeStruct = {
      ...pre,
    };

    const ir = next.ir as IContainerInfo;

    if (!ir.statements || ir.statements.length === 0) {
      return next;
    }

    // 将 statements 转换为 EnhancedStatement 类型
    const statements = ir.statements as EnhancedStatement[];

    // 1. 推断需要导出的 statements
    const exportedIds = inferExportedStatements(statements);

    // 2. 按 targetFile 分组
    const grouped = groupStatementsByTargetFile(statements);

    // 3. 分析跨文件依赖
    const internalDeps = analyzeInternalDependencies(statements);

    const allChunks: ICodeChunk[] = [];
    const allImports: Record<string, Record<string, ImportSpec>> = {}; // subModule -> imports
    const subModuleToFileType: Record<string, string> = {}; // subModule -> fileType

    // 4. 为每个目标文件生成 chunks
    for (const [targetFile, stmts] of grouped) {
      const subModule = getSubModuleFromTargetFile(targetFile);
      const fileType = getFileTypeFromTargetFile(targetFile);

      // 记录 subModule -> fileType 映射，以供第 5 步使用
      subModuleToFileType[subModule] = fileType;

      // 4.1 收集该文件的外部依赖
      const fileExternalImports = collectExternalDependencies(stmts);

      // 存储每个 subModule 的导入信息，稍后统一处理
      if (Object.keys(fileExternalImports).length > 0) {
        allImports[subModule] = fileExternalImports;
      }

      // 4.2 生成内部导入 chunks（从其他文件导入）
      const depsFromOtherFiles = internalDeps.get(targetFile);
      if (depsFromOtherFiles) {
        for (const [fromFile, names] of depsFromOtherFiles) {
          const importContent = generateInternalImportStatement(fromFile, Array.from(names));
          allChunks.push({
            type: ChunkType.STRING,
            fileType,
            subModule,
            name: COMMON_CHUNK_NAME.InternalDepsImport,
            content: importContent,
            linkAfter: [COMMON_CHUNK_NAME.ExternalDepsImport],
          });
        }
      }

      // 4.3 生成 statement chunks
      for (const stmt of stmts) {
        // 获取代码内容
        let content = '';
        if (stmt.source) {
          content = stmt.source;
        } else if (stmt.value) {
          content = isArray(stmt.value) ? stmt.value.join('') : stmt.value;
        }

        // 判断是否需要导出（非 index.tsx 且被其他文件引用）
        const needExport = exportedIds.has(stmt.id) && targetFile !== 'index.tsx';
        if (needExport) {
          const exportedContent = addExportKeyword(content);
          // 确保 content 是字符串类型
          content = Array.isArray(exportedContent) ? exportedContent.join('\n') : exportedContent;
        }

        // 判断 private 属性（默认为 true，即放在组件内部）
        const isPrivate = stmt.private !== false;

        const chunkName = getChunkNameForStatement(targetFile, subModule, isPrivate);

        allChunks.push({
          type: ChunkType.STRING,
          fileType,
          subModule,
          name: chunkName,
          content,
          linkAfter: getLinkAfterForStatement(targetFile, isPrivate),
        });
      }
    }

    // 4.5 处理 index.tsx 可能未被 grouped 覆盖的跨文件内部导入
    // （当 index.tsx 没有任何 statement 但有来自其他文件的引用时）
    const indexInternalDeps = internalDeps.get('index.tsx');
    if (indexInternalDeps && !grouped.has('index.tsx')) {
      for (const [fromFile, names] of indexInternalDeps) {
        const importContent = generateInternalImportStatement(fromFile, Array.from(names));
        allChunks.push({
          type: ChunkType.STRING,
          fileType: 'tsx',
          subModule: 'index',
          name: COMMON_CHUNK_NAME.InternalDepsImport,
          content: importContent,
          linkAfter: [COMMON_CHUNK_NAME.ExternalDepsImport],
        });
      }
    }

    // 5. 处理外部导入
    // 对于 index.tsx (默认 subModule)，保持原有逻辑通过 next.imports 合并
    // 对于其他 subModule，生成独立的导入 chunks
    for (const [subModule, imports] of Object.entries(allImports)) {
      if (subModule === 'index') {
        // index.tsx 使用全局 imports 处理方式
        next.imports = mergeImportMaps({}, next.imports, imports);
      } else {
        // 其他文件生成独立的外部导入 chunks
        for (const [pkg, spec] of Object.entries(imports)) {
          if (!pkg) continue;

          const importLines: string[] = [];
          if (spec.defaultImport) {
            importLines.push(`import ${spec.defaultImport} from '${pkg}';`);
          }
          if (spec.namespaceImport) {
            importLines.push(`import * as ${spec.namespaceImport} from '${pkg}';`);
          }
          if (spec.namedImports.size > 0) {
            const names = Array.from(spec.namedImports).join(', ');
            importLines.push(`import { ${names} } from '${pkg}';`);
          }

          if (importLines.length > 0) {
            const fileType = subModuleToFileType[subModule] ?? FileType.TS;

            allChunks.push({
              type: ChunkType.STRING,
              fileType,
              subModule,
              name: COMMON_CHUNK_NAME.ExternalDepsImport,
              content: importLines.join('\n'),
              linkAfter: [],
            });
          }
        }
      }
    }

    next.chunks.push(...allChunks);
    return next;
  };

  return plugin;
};

export default pluginFactory;
